import type {
  ShippingRuleCondition,
  ConditionField,
  ConditionOperator,
  ConditionValue,
} from '@/types';
import type { ProvinceOption } from './provinceLookup';

type TLike = (key: string, opts?: Record<string, unknown>) => string;

/**
 * RS-20 — Plain-language summary of a rule's conditions for the Info
 * column (PRD §6.3).
 *
 * Examples:
 *   Source: BG
 *   Source: BG or ER
 *   Destination is not BG
 *   Fragile: Yes
 *   Qlue Verification: No
 *   COD Amount > 50000
 *   Source: Cairo, Package Size is not XL, Fragile: Yes
 *
 * The translation table keys live under the `shipping_rules` namespace.
 * Unknown keys fall through to the key itself (i18next's default), so a
 * missing translation degrades to a still-readable summary instead of
 * crashing the table.
 */
export function formatRuleSummary(
  conditions: ShippingRuleCondition[],
  t: TLike,
  provinces: ProvinceOption[] = [],
): string {
  if (!conditions.length) {
    return t('shipping_rules.summary.empty');
  }

  const join = t('shipping_rules.summary.join') || ', ';
  return conditions.map((c) => formatOne(c, t, provinces)).join(join);
}

function formatOne(c: ShippingRuleCondition, t: TLike, provinces: ProvinceOption[]): string {
  const fieldLabel = t(`shipping_rules.fields.${c.field as ConditionField}`);

  switch (c.operator) {
    case 'is':
      return `${fieldLabel}: ${formatValueAsAny(c.field, c.value, t, provinces)}`;
    case 'is_not':
      return `${fieldLabel} ${t('shipping_rules.operators.is_not')} ${formatValueAsAny(
        c.field,
        c.value,
        t,
        provinces,
      )}`;
    case 'gt':
      return `${fieldLabel} ${t('shipping_rules.operators.gt')} ${numericValue(c.value)}`;
    case 'lt':
      return `${fieldLabel} ${t('shipping_rules.operators.lt')} ${numericValue(c.value)}`;
    case 'gte':
      return `${fieldLabel} ${t('shipping_rules.operators.gte')} ${numericValue(c.value)}`;
    case 'lte':
      return `${fieldLabel} ${t('shipping_rules.operators.lte')} ${numericValue(c.value)}`;
    case 'eq':
      return `${fieldLabel} ${t('shipping_rules.operators.eq')} ${numericValue(c.value)}`;
  }
  return fieldLabel; // unknown operator — defensive fallback
}

/** Render the value side for IS / IS_NOT — handles bools, single + multi-value. */
function formatValueAsAny(
  field: ConditionField | string,
  value: ConditionValue,
  t: TLike,
  provinces: ProvinceOption[] = [],
): string {
  // Boolean fields (qlue, fragile, temperature_control) — translate to Yes/No.
  if (field === 'qlue' || field === 'fragile' || field === 'temperature_control') {
    const v = Array.isArray(value) ? (value as boolean[])[0] : (value as boolean);
    return v ? t('shipping_rules.bool.true') : t('shipping_rules.bool.false');
  }

  // For source/destination, swap province codes for the localized name.
  const decode = (raw: unknown): string => {
    if (field === 'source' || field === 'destination') {
      const hit = provinces.find((p) => p.value === String(raw));
      if (hit) return hit.label;
    }
    return String(raw);
  };

  if (Array.isArray(value)) {
    if (value.length === 1) return decode(value[0]);
    const or = t('shipping_rules.summary.or') || ' or ';
    return value.map(decode).join(or);
  }

  return decode(value);
}

function numericValue(value: ConditionValue): string {
  if (Array.isArray(value)) return String(value[0] ?? '');
  return String(value);
}

// Re-export types so callers that import the util don't need a second import.
export type { ConditionField, ConditionOperator, ShippingRuleCondition };
